import { ActionDialog, Notification } from "@/components"
import { Grid, Typography } from "@material-ui/core"
import React, { useState } from "react"
import { UserInstance } from "@/services/User"
import InstanceListItem, { InstanceListItemView } from "@/components/InstanceSelectorDialogue/InstanceListItem"
import { useGlobalStateStore } from "@/store/GlobalState"
import ApiService from "@/services/Api"
import { IQuickReply } from "@/protocols/messages"

type EnrichedUserInstance = UserInstance & {
	viewType: InstanceListItemView
}

type ImportQuickRepliesDialogProps = {
	onChange: (quickReplies: IQuickReply[]) => void
	disabled?: boolean
}

type InstancesRelatedToImportData = {
	sourceInstance: {
		id?: number
		name?: string
	}
	targetInstance: {
		id?: number
		name?: string
	}
}

const ImportQuickRepliesDialog: React.FC<ImportQuickRepliesDialogProps> = (props) => {
	const { children, onChange, disabled } = props

	const globalStateStore = useGlobalStateStore()
	const allUserInstances = globalStateStore.user.user_instances

	const [openDialog, setOpenDialog] = useState(false)
	const [openConfirmImportDialog, setOpenConfirmImportDialog] = useState(false)
	const [instancesRelatedToImportData, setInstancesDataInstancesRelatedToImportData] = useState<InstancesRelatedToImportData>()

	const targetInstanceLabel = `${instancesRelatedToImportData?.targetInstance.id} | ${instancesRelatedToImportData?.targetInstance.name}`

	const sourceInstanceLabel = `${instancesRelatedToImportData?.sourceInstance.id} | ${instancesRelatedToImportData?.sourceInstance.name}`

	const importQuickRepliesText = `Tem certeza que deseja importar todas as respostas rápidas da instância ${targetInstanceLabel} para a instância ${sourceInstanceLabel} ?`

	const handleOpenImportQuickRepliesDialog = () => {
		if (disabled) {
			return
		}

		setOpenDialog(true)
	}

	const handleCloseImportQuickRepliesDialog = () => {
		setOpenDialog(false)
	}

	const handleOpenConfirmImportQuickRepliesDialog = () => {
		setOpenConfirmImportDialog(true)
	}

	const handleCloseConfirmImportQuickRepliesDialog = () => {
		setOpenConfirmImportDialog(false)
	}

	const getEnrichedUserInstances = (userInstances: UserInstance[]): EnrichedUserInstance[] => {
		const enrichedUserInstances: EnrichedUserInstance[] = userInstances.map((userInstance) => {
			return {
				...userInstance,
				viewType: "import_instance_quick_reply_item"
			}
		})

		return enrichedUserInstances
	}

	const enrichedUserInstances = getEnrichedUserInstances(allUserInstances)

	const importQuickReplies = async () => {
		try {
			const { data } = await ApiService.post(`/quick-reply/import/from-instance/${instancesRelatedToImportData?.sourceInstance.id}/to-instance/${instancesRelatedToImportData?.targetInstance.id}`)

			if (data.length === 0) {
				Notification.warning({ message: "Não há respostas rápidas a serem importadas." })
			} else {
				onChange(data)
				Notification.success({ message: "Respostas rápidas importadas com sucesso." })
			}
		} catch (error) {
			Notification.error({ message: "Não foi possível importar as respostas rápidas." })
		}
		handleCloseImportQuickRepliesDialog()
		handleCloseConfirmImportQuickRepliesDialog()
	}

	const handleOpenConfirmationDialog = (sourceInstanceId: number, instanceName?: string) => {
		const sourceInstanceName = globalStateStore.instance?.nickname
		const targetInstanceName = instanceName

		setInstancesDataInstancesRelatedToImportData({
			sourceInstance: {
				id: sourceInstanceId,
				name: sourceInstanceName
			},
			targetInstance: {
				id: globalStateStore.instance.instance_id,
				name: String(targetInstanceName)
			}
		})

		handleOpenConfirmImportQuickRepliesDialog()
	}

	const renderedChildren = React.isValidElement(children)
		? React.cloneElement(children as React.ReactElement, { disabled })
		: children

	return (
		<>
			<Grid item onClick={handleOpenImportQuickRepliesDialog}>
				{renderedChildren}
			</Grid>
			<ActionDialog
				title="Importar respostas rápidas"
				openDialog={openDialog}
				onClose={handleCloseImportQuickRepliesDialog}
			>
				<Grid
					container
					spacing={1}
				>
					{enrichedUserInstances.map((enrichedUserInstance) => {
						const idOfInstanceCurrentlyIterated = enrichedUserInstance.instance_id

						const idOfInstanceUserIsCurrentlyLoggedIn = globalStateStore.instance.instance_id

						const isCurrentlyIteratedInstanceAValidSourceInstance = idOfInstanceCurrentlyIterated !== idOfInstanceUserIsCurrentlyLoggedIn

						if (isCurrentlyIteratedInstanceAValidSourceInstance) {
							return (
								<Grid
									item
									xs={12}
									key={idOfInstanceCurrentlyIterated}
								>
									<InstanceListItem
										targetInstanceId={enrichedUserInstance.instance_id}
										targetInstanceName={String(enrichedUserInstance.nickname)}
										targetInstanceRoleName={enrichedUserInstance.user_role_name}
										listedView={enrichedUserInstance.viewType}
										onClick={() => handleOpenConfirmationDialog(enrichedUserInstance.instance_id, String(enrichedUserInstance.nickname))}
									/>
								</Grid>
							)
						}
					})}
				</Grid>
			</ActionDialog>
			<ActionDialog
				title="Respostas rápidas"
				openDialog={openConfirmImportDialog}
				onSave={importQuickReplies}
				saveText="IMPORTAR"
				onClose={handleCloseConfirmImportQuickRepliesDialog}
			>
				<Grid container spacing={2}>

					<Grid item>
						<Typography>
							{importQuickRepliesText}
						</Typography>
					</Grid>
					<Grid item>
						<Grid container direction="column" spacing={1}>
							<Grid item>
								<Typography style={{ fontWeight: 700 }}>
									ATENÇÃO:
								</Typography>
							</Grid>
							<Grid item>
								A importação será feita com base no nome das respostas rápidas. Caso exista alguma resposta rápida com nome exatamente igual entre as instâncias, <strong>a resposta rápida não será importada.</strong>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</ActionDialog>
		</>
	)
}

export default ImportQuickRepliesDialog
